<script setup lang="ts">
import { ref, watch } from 'vue'
import { useDebounceFn } from '@vueuse/core'

import { getPages } from '@voix/composables/queries/useQueryPages'
import { usePagesStore } from '@voix/store/pagesStore'

import TextInput from '@voix/components/chrome/controls/TextInput.vue'

defineProps({
  label: {
    type: String,
    default: 'Search for a page',
  },
  pageUrl: {
    type: String,
    required: false,
  },
  pageId: {
    type: String,
    required: false,
  },
})

const emit = defineEmits(['update:pageUrl', 'update:pageId', 'update:model-value'])

const pageStore = usePagesStore()
const search = ref('')
const modelValue = defineModel()

pageStore.pages.paginatorInfo = {}
pageStore.pages.data = []

if (modelValue.value) {
  getPages({
    filter: `[{\"field\":\"id\",\"op\":\"=\",\"value\":\"${modelValue.value}\",\"clause\":\"where\"}]`,
    orderBy: [{ column: 'TITLE', order: 'ASC' }],
    first: 10,
    page: 1,
  }).then(() => {
    const pageUrl = pageStore.pages.data.find(page => page.id === modelValue.value)?.path
    if (pageUrl)
      emit('update:pageUrl', pageUrl)
  })
}

const requestPages = useDebounceFn(() => {
  getPages({
    filter: `[{\"field\":\"title\",\"op\":\"like\",\"value\":\"%${search.value}%\",\"clause\":\"where\"}]`,
    orderBy: [{ column: 'TITLE', order: 'ASC' }],
    first: 10,
    page: 1,
  })
}, 1000)

watch(search, () => {
  requestPages()
})

function clearResults() {
  search.value = ''
  pageStore.pages.data = []
}

function selectPage(page: { id: string, title: string }) {
  modelValue.value = page
  emit('update:model-value', page)

  if (page) {
    const pageUrl = pageStore.pages.data.find(p => p.id === page.id)?.path
    if (pageUrl)
      emit('update:pageUrl', pageUrl)

    const pageId = pageStore.pages.data.find(p => p.id === page.id)?.id
    if (pageId)
      emit('update:pageId', pageId.toString())
  }

  clearResults()
}
</script>

<template>
  <div class="relative">
    <TextInput
      id="page-search"
      v-model="search"
      placeholder="Page Name to search for"
      :label="label"
      autocomplete="off"
      aria-autocomplete="none"
    />

    <div v-if="pageStore.pages.data && pageStore.pages.data.length > 0 && search" class="absolute z-10 top-0 left-0 right-0 translate-y-16 bg-white rounded max-h-[40vh] overflow-x-hidden overflow-y-scroll">
      <div class="flex items-center justify-between text-xs bg-slate-100 text-slate-400 py-2 px-2">
        <div>Search results - select below</div>
        <button @click="clearResults">
          <Icon name="heroicons:x-mark-20-solid" class="h-6 w-6" />
        </button>
      </div>
      <div>
        <ul v-for="page in pageStore.pages.data" :key="page.id" class="flex flex-col divide-y divide-slate-300">
          <li class="text-xs text-gray-600 hover:bg-slate-100 py-2 px-2 cursor-pointer" @click="selectPage(page)">
            {{ page.title }}
            <span v-if="page?.in_requested_release" class="py-0.5 px-2 rounded-full bg-emerald-100 text-emerald-600">Version Included</span>
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>
